import React, { createContext, useContext, useEffect, useState } from 'react';

export interface VersionInfo {
  version: string;
  date?: string;
  changes?: string[];
}

interface VersionContextValue {
  info: VersionInfo | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const VersionContext = createContext<VersionContextValue | undefined>(undefined);

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return await res.json();
  } catch (e) {
    return null;
  }
}

export const VersionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const v = await fetchVersion();
    if (!v) setError('Kunde inte hämta version');
    setInfo(v);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <VersionContext.Provider value={{ info, loading, error, refresh: load }}>
      {children}
    </VersionContext.Provider>
  );
};

export function useVersionInfo() {
  const ctx = useContext(VersionContext);
  if (!ctx) throw new Error('useVersionInfo must be used within VersionProvider');
  return ctx;
}
